<template>
  <div class="container-fluid p-4">
    <!-- Welcome Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="fade-in">
          <h2 class="mb-2 fw-bold gradient-text">{{ getWelcomeMessage() }}</h2>
          <p class="text-muted">{{ getSubtitleMessage() }}</p>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="nav-pills">
          <button
            v-for="tab in studentTabs"
            :key="tab.key"
            class="nav-pill"
            :class="{ active: activeTab === tab.key }"
            @click="setActiveTab(tab.key)"
          >
            <i :class="`bi ${tab.icon}`"></i>
            {{ tab.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Home Tab - Original Dashboard Content -->
      <div v-if="activeTab === 'home'" class="container-fluid">
        <!-- Header Greeting and Emotion Check -->
        <div class="row mb-4">
          <div class="col-md-8">
            <div class="greeting-section">
              <div class="d-flex align-items-center mb-3">
                <div class="greeting-icon me-3">👋</div>
                <div>
                  <h3 class="mb-1 fw-bold">Hey, {{ currentUser?.name || 'Anaya' }}!</h3>
                  <p class="mb-0 text-muted">Ready to learn today?</p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="emotion-card p-3">
              <p class="mb-2 fw-semibold">How are you feeling?</p>
              <div class="emotion-selector d-flex gap-2">
                <div
                  v-for="emotion in emotions"
                  :key="emotion.name"
                  class="emotion-item"
                  :class="{ selected: selectedEmotion === emotion.name }"
                  @click="selectEmotion(emotion.name)"
                >
                  {{ emotion.emoji }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content Grid -->
        <div class="row g-4 mb-4">
          <!-- My Badges -->
          <div class="col-lg-4">
            <div class="dashboard-card badges-card p-4">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h5 class="mb-0 fw-bold">My Badges</h5>
                <div class="badge-stars">
                  <span v-for="n in earnedBadges.length" :key="n" class="star-filled">⭐</span>
                </div>
              </div>
              <p class="badge-count mb-3">{{ earnedBadges.length }} / {{ totalBadges }} Earned</p>
              <div class="progress-container mb-3">
                <div class="progress-bar-custom">
                  <div 
                    class="progress-fill" 
                    :style="{ width: badgeProgress + '%' }"
                  ></div>
                </div>
              </div>
              <button class="btn btn-view-badges w-100" @click="viewAllBadges">
                View All Badges →
              </button>
            </div>
          </div>

          <!-- Priority Task (card always visible; shows placeholder when absent) -->
          <div class="col-lg-4">
            <div class="dashboard-card priority-task-card p-4">
              <div class="d-flex align-items-center mb-3">
                <div class="task-icon me-3">📚</div>
                <div>
                  <h6 class="mb-1 fw-bold">{{ priorityTask ? priorityTask.title : 'No priority task today' }}</h6>
                  <p class="mb-0 task-label">{{ priorityTask ? 'Priority Task' : 'You are all caught up' }}</p>
                </div>
              </div>
              <!-- Show a progress ring only if backend provides progress fields; otherwise show status/due time -->
              <div class="progress-circle-container text-center mb-3" v-if="priorityTask && priorityTask.progress !== undefined && priorityTask.total !== undefined">
                <div class="progress-circle">
                  <div class="progress-text">
                    <span class="progress-number">{{ priorityTask.progress }}/{{ priorityTask.total }}</span>
                    <small class="progress-unit">completed</small>
                  </div>
                </div>
              </div>
              <div v-else-if="priorityTask" class="mb-3">
                <span class="task-label">
                  {{ priorityTask.status }}<span v-if="priorityTask.dueTime"> • {{ priorityTask.dueTime }}</span>
                </span>
              </div>
              <div v-else class="mb-3">
                <span class="task-label">Assigned tasks will appear here.</span>
              </div>
              <button
                v-if="priorityTask && !priorityTask.completed"
                class="btn btn-mark-done w-100"
                @click="completePriorityTask"
              >
                Mark as Done
              </button>
            </div>
          </div>

          <!-- Explore Stories -->
          <div class="col-lg-4">
            <div class="dashboard-card stories-card p-4">
              <div class="story-content">
                <div class="story-icon mb-3">{{ featuredStory.thumbnail || '📖' }}</div>
                <h6 class="fw-bold mb-2">{{ featuredStory.title || 'No Stories Available' }}</h6>
                <p class="story-meta mb-2" v-if="featuredStory.title">
                  {{ featuredStory.readingLevel || (featuredStory.level ? `Level ${featuredStory.level}` : 'Level 1') }} • {{ featuredStory.pages || featuredStory.page_cnt || 1 }} page{{ (featuredStory.pages || featuredStory.page_cnt || 1) !== 1 ? 's' : '' }}
                </p>
                <p class="story-description mb-3">
                  {{ featuredStory.summary || (featuredStory.title ? 'Continue your reading adventure with this exciting story!' : 'Stories will appear here when available.') }}
                </p>
                <button 
                  v-if="featuredStory.title" 
                  class="btn btn-read-now w-100" 
                  @click="readFeaturedStory"
                >
                  Read Now →
                </button>
                <button 
                  v-else 
                  class="btn btn-read-now w-100 opacity-50" 
                  disabled
                >
                  No Stories Available
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Leaderboard and Daily Insights -->
        <div class="row g-4 mb-4">
          <div class="col-lg-6">
            <div class="dashboard-card leaderboard-card p-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 fw-bold">Leaderboard Snapshot</h5>
                <a href="#" class="view-all-link" @click.prevent="openJoinClass">View All →</a>
              </div>
              <div class="leaderboard-list">
                <div 
                  v-for="(student, index) in leaderboard" 
                  :key="student.id"
                  class="leaderboard-item d-flex align-items-center mb-3"
                >
                  <div class="rank-badge me-3" :class="`rank-${index + 1}`">
                    {{ index + 1 }}
                  </div>
                  <div class="student-avatar me-3">
                    <span class="avatar-emoji">{{ student.avatar }}</span>
                  </div>
                  <div class="flex-grow-1">
                    <p class="mb-0 fw-semibold">{{ student.name }}</p>
                  </div>
                  <div class="points">
                    <span class="fw-bold">{{ student.points }} pts</span>
                  </div>
                </div>
              </div>
              <div v-if="leaderboard.length === 0" class="alert alert-info mt-2">
                Join a class to see your ranking among classmates!
                <button class="btn btn-sm btn-primary ms-2" @click="openJoinClass">Join Class</button>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="dashboard-card insights-card p-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 fw-bold">Daily Insights</h5>
                <p class="mb-0 text-muted small">Choose an activity</p>
              </div>
              <div class="insights-grid">
                <div 
                  v-for="activity in dailyActivities" 
                  :key="activity.id"
                  class="activity-item text-center"
                  @click="selectActivity(activity.type)"
                >
                  <div class="activity-icon mb-2" :style="{ background: activity.color }">
                    {{ activity.icon }}
                  </div>
                  <p class="mb-0 small fw-semibold">{{ activity.name }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Upcoming Tasks -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="dashboard-card p-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 fw-bold">Upcoming Tasks</h5>
                <button class="btn btn-link p-0 text-muted">
                  <i class="bi bi-chevron-up"></i>
                </button>
              </div>
              <div class="row g-3">
                <div 
                  v-for="task in upcomingTasks" 
                  :key="task.id" 
                  class="col-md-3"
                >
                  <div 
                    class="task-card p-3" 
                    :style="{ background: task.color, borderRadius: '12px' }"
                  >
                    <div class="text-center text-white">
                      <div class="task-icon-large mb-2">{{ task.icon }}</div>
                      <h6 class="mb-1 fw-bold">{{ task.title }}</h6>
                      <p class="mb-3 small">{{ task.dueTime }}</p>
                      <button 
                        class="btn btn-task w-100" 
                        :style="{ background: task.buttonColor }"
                        @click="goToTask(task.id)"
                      >
                        Go To Task
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Weekly Challenge (card always visible; shows placeholder when absent) -->
        <div class="row">
          <div class="col-12">
            <div class="dashboard-card challenge-card p-4" role="region" aria-label="Weekly Challenge">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="challenge-icon me-3">🏆</div>
                  <div>
                    <h6 class="mb-1 fw-bold">{{ weeklyChallenge ? (weeklyChallenge.title || 'Weekly Challenge') : 'No active challenges' }}</h6>
                    <p class="mb-0 text-muted">{{ weeklyChallenge ? (weeklyChallenge.description || 'Keep going!') : 'Join a challenge to see progress here.' }}</p>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-3">
                  <div class="challenge-progress">
                    <span class="fw-bold">{{ weeklyChallenge && weeklyChallenge.progress !== undefined ? weeklyChallenge.progress : 0 }}%</span>
                    <small class="text-muted">completed</small>
                  </div>
                  <div class="d-flex gap-2">
                    <button type="button" class="btn btn-outline-challenge" @click="browseChallenges">Browse Challenges</button>
                    <button type="button" class="btn btn-challenge" @click="viewChallenge">View Challenge</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Planner Page -->
      <div v-else-if="activeTab === 'planner'">
        <PlannerPage @tasks-updated="refreshDashboardData" />
      </div>

      <!-- Rewards Page -->
      <div v-else-if="activeTab === 'rewards'">
        <RewardsPage />
      </div>

      <!-- Library Page -->
      <div v-else-if="activeTab === 'library'">
        <LibraryPage />
      </div>

      <!-- Activity Page -->
      <div v-else-if="activeTab === 'activity'">
        <ActivityPage />
      </div>

      <!-- Language Learning Page -->
      <div v-else-if="activeTab === 'language-learning'">
        <LanguageLearningPage />
      </div>

      <!-- Coming Soon -->
      <div v-else class="modern-card p-4 text-center">
        <h5 class="mb-3" style="color: var(--text-primary);">Coming Soon!</h5>
        <p style="color: var(--text-secondary);">This feature is under development.</p>
      </div>
    </div>
  </div>
</template>

<script>
import PlannerPage from './PlannerPage.vue'
import RewardsPage from './RewardsPage.vue'
import LibraryPage from './LibraryPage.vue'
import ActivityPage from './ActivityPage.vue'
import LanguageLearningPage from './LanguageLearningPage.vue'

export default {
  name: 'StudentDashboard',
  components: {
    PlannerPage,
    RewardsPage,
    LibraryPage,
    ActivityPage,
    LanguageLearningPage
  },
  data() {
    return {
      tasks: [],
      badges: [],
      stories: [],
      priorityTask: null, // Derived from backend tasks
      weeklyChallenge: null, // Derived from backend user challenges
      totalBadges: 0, // Total available badges for the user (completed + in_progress + not_begun)
      selectedEmotion: '',
      emotions: [
        { emoji: '🎁', name: 'excited' },
        { emoji: '😄', name: 'happy' },
        { emoji: '😊', name: 'content' },
        { emoji: '😴', name: 'tired' }
      ],
      // Deprecated local demo progress; will rely on backend when available
      readingProgress: { current: 0, total: 0 },
      leaderboard: [],
      dailyActivities: [
        {
          id: 1,
          name: 'Puzzle',
          icon: '🧩',
          color: '#60a5fa',
          type: 'puzzle'
        },
        {
          id: 2,
          name: 'Word',
          icon: 'A',
          color: '#34d399',
          type: 'word'
        },
        {
          id: 3,
          name: 'Thought',
          icon: '💭',
          color: '#a78bfa',
          type: 'thought'
        },
        {
          id: 4,
          name: 'Dictation',
          icon: '🎤',
          color: '#fbbf24',
          type: 'dictation'
        }
      ],
      upcomingTasks: [],
      activeTab: 'home',
      studentTabs: [
        { key: 'home', label: 'Home', icon: 'bi-house-fill' },
        { key: 'planner', label: 'Planner', icon: 'bi-calendar3' },
        { key: 'rewards', label: 'Rewards', icon: 'bi-gift' },
        { key: 'library', label: 'Library', icon: 'bi-book' },
        { key: 'activity', label: 'Activity', icon: 'bi-play-circle' },
        { key: 'language-learning', label: 'Language Learning', icon: 'bi-translate' }
      ]
    }
  },
  computed: {
    currentUser() {
      const storedUser = localStorage.getItem('currentUser')
      return storedUser ? JSON.parse(storedUser) : null
    },
    earnedBadges() {
      const earned = this.badges.filter(badge => badge.earned)
      return earned
    },
    badgeProgress() {
      const total = this.totalBadges || 0
      if (!total) return 0
      return Math.round((this.earnedBadges.length / total) * 100)
    },
    featuredStory() {
      // Get the first available story from the stories array
      if (this.stories && Array.isArray(this.stories) && this.stories.length > 0) {
        const firstStory = this.stories[0]
        return firstStory
      }
      
      // Temporary fallback while debugging - remove this later
      if (!this.stories || this.stories.length === 0) {
        return {
          title: 'Emotion Detectives',
          summary: 'Continue your reading adventure with this exciting story!',
          level: 3,
          page_cnt: 7,
          readingLevel: 'Level 3',
          pages: 7,
          thumbnail: '📖'
        }
      }
      
      // Return empty object if no stories available
      return {}
    }
  },
  async mounted() {
    // Load student data when component mounts
    // Authentication is already handled by router guard
    if (this.currentUser?.role === 'student') {
      await this.loadStudentData()
      await this.loadChallengeData()
      this.loadTaskProgress()
    }

    // If returning from challenge detail where progress was updated, refresh banner
    try {
      if (localStorage.getItem('challengeUpdated') === '1') {
        await this.loadChallengeData()
        localStorage.removeItem('challengeUpdated')
      }
    } catch (e) {
      // ignore refresh errors
    }

    // Set up refresh listener
    this.setupRefreshListeners()
  },
  
  beforeUnmount() {
    // Clean up event listeners
    this.cleanupRefreshListeners()
  },
  methods: {
    async openJoinClass() {
      // TODO: Replace with proper modal - for now use better prompt
      const classCode = prompt('Enter your class code to join:')
      if (!classCode || !classCode.trim()) return
      
      try {
        const { enrollStudent } = await import('../services/teacherService.js')
        const res = await enrollStudent({ class_code: classCode.trim(), student_id: this.currentUser?.id })
        
        if (res?.success) {
          this.showFeedback(`${res.message || 'Enrolled successfully!'} 🎉 You're now part of the class leaderboard!`, 'success')
          
          // Give the backend a moment to process enrollment, then refresh all data
          setTimeout(async () => {
            await this.refreshDashboardData()
            
            // If leaderboard is still empty after refresh, show current student as fallback
            if (this.leaderboard.length === 0) {
              this.leaderboard = [{
                id: this.currentUser?.id || 0,
                name: this.currentUser?.first_name || this.currentUser?.full_name || 'You',
                points: this.currentUser?.points || 0,
                avatar: this.getStudentAvatar(this.currentUser?.id, true)
              }]
            }
          }, 1000) // 1 second delay to allow backend processing
        } else {
          this.showFeedback(res?.error || res?.message || 'Enrollment failed', 'error')
        }
      } catch (e) {
        console.error('Enrollment failed:', e)
        
        // Check if it's a network error vs API error
        if (e.response) {
          const errorMsg = e.response.data?.error || e.response.data?.message || `Server error: ${e.response.status}`
          this.showFeedback(`Enrollment failed: ${errorMsg}`, 'error')
        } else if (e.request) {
          this.showFeedback('Could not connect to server. Please check your internet connection.', 'error')
        } else {
          this.showFeedback('Could not enroll. Please verify the class code and try again.', 'error')
        }
      }
    },
    async loadStudentData() {
      try {
        // Import backend services
        const [{ getUserTasks, getUpcomingTasks }, { getUserBadges }, { getSuggestions }, { getLeaderboard }] = await Promise.all([
          import('../services/taskService.js'),
          import('../services/badgeService.js'),
          import('../services/readingService.js'),
          import('../services/leaderboardService.js')
        ])
        const [tasks, badgesData, stories, leaderboardData, upcoming] = await Promise.all([
          getUserTasks(this.currentUser.id).catch(e => { 
            console.error('Failed to load tasks:', e)
            this.showFeedback('Could not load your tasks. Please refresh the page.', 'warning')
            return [] 
          }),
          getUserBadges(this.currentUser.id).catch(e => { 
            console.error('Failed to load badges:', e)
            this.showFeedback('Could not load your badges. Please refresh the page.', 'warning')
            return { completed: [], in_progress: [], not_begun: [] } 
          }),
          getSuggestions(this.currentUser.id).catch(e => { 
            console.error('Failed to load stories:', e)
            return [] 
          }),
          getLeaderboard(this.currentUser.id).catch(e => { 
            console.error('Failed to load leaderboard:', e)
            return [] 
          }),
          getUpcomingTasks(this.currentUser.id).catch(e => { 
            console.error('Failed to load upcoming tasks:', e)
            this.showFeedback('Could not load upcoming tasks. Please refresh the page.', 'warning')
            return [] 
          })
        ])
        
        // Update the component's data
        this.tasks = tasks
        // Pick a priority task (e.g., highest priority, not completed). Adjust criteria as needed.
        this.priorityTask = (this.tasks || [])
          .filter(t => !t.completed)
          .sort((a, b) => (b.priority || 0) - (a.priority || 0))[0] || null
        this.badges = [
          ...(badgesData.completed || []).map(b => ({ ...b, earned: true })),
          ...(badgesData.in_progress || []).map(b => ({ ...b, earned: false })),
        ]
        this.totalBadges =
          (badgesData.completed?.length || 0) +
          (badgesData.in_progress?.length || 0) +
          (badgesData.not_begun?.length || 0)
        this.stories = stories
        this.upcomingTasks = (upcoming || []).slice(0, 4).map((a, idx) => ({
          id: a.id || idx,
          title: a.title || 'Task',
          dueTime: a.due_date ? `Due ${new Date(a.due_date).toLocaleDateString()}` : '',
          icon: '📚',
          color: '#3b82f6',
          buttonColor: '#1e40af'
        }))
        
        // Handle leaderboard data with fallback for single student
        if (Array.isArray(leaderboardData) && leaderboardData.length > 0) {
          this.leaderboard = leaderboardData.slice(0, 3).map((row, idx) => ({
            id: row.user_id || idx,
            name: row.full_name || 'Student',
            points: row.points || 0,
            avatar: this.getStudentAvatar(row.user_id, row.user_id === this.currentUser?.id)
          }))
        } else {
          // If no leaderboard data, keep empty array to show "join class" message
          this.leaderboard = []
        }
      } catch (error) {
        console.error('Failed to load student data:', error)
        this.showFeedback('Failed to load dashboard data. Please refresh the page.', 'error')
      }
    },
    
    // Method to refresh dashboard data (can be called from other components)
    async refreshDashboardData() {
      await this.loadStudentData()
      await this.loadChallengeData()
      this.loadTaskProgress()
    },
    
    // Setup event listeners for data refresh
    setupRefreshListeners() {
      // Listen for when tab becomes visible again
      this.visibilityChangeHandler = () => {
        if (!document.hidden) {
          const lastUpdate = localStorage.getItem('tasksUpdated')
          if (lastUpdate && Date.now() - parseInt(lastUpdate) < 30000) { // Within 30 seconds
            this.refreshDashboardData()
            localStorage.removeItem('tasksUpdated')
          }
        }
      }
      document.addEventListener('visibilitychange', this.visibilityChangeHandler)
      
      // Listen for focus events (user switching back to tab)
      this.focusHandler = () => {
        const lastUpdate = localStorage.getItem('tasksUpdated')
        if (lastUpdate && Date.now() - parseInt(lastUpdate) < 30000) {
          this.refreshDashboardData()
          localStorage.removeItem('tasksUpdated')
        }
      }
      window.addEventListener('focus', this.focusHandler)
    },
    
    cleanupRefreshListeners() {
      if (this.visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
      }
      if (this.focusHandler) {
        window.removeEventListener('focus', this.focusHandler)
      }
    },
    async loadChallengeData() {
      try {
        const { getUserChallenges, getAllChallengesForUser } = await import('../services/challengeService.js')
        const res = await getUserChallenges(this.currentUser.id)
        // Normalize various response shapes
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : []
        // If user has none joined, optionally look at all available ones (non-blocking)
        let candidateList = list
        if (candidateList.length === 0) {
          try {
            const allRes = await getAllChallengesForUser(this.currentUser.id)
            candidateList = Array.isArray(allRes?.data) ? allRes.data : []
          } catch { /* ignore */ }
        }
        const weekly = (candidateList || []).find(c => (c.category || '').toLowerCase().includes('week') || String(c.duration || '').includes('7'))
        this.weeklyChallenge = weekly || (candidateList[0] || null)
      } catch (e) {
        console.error('Failed to load challenges:', e)
        this.weeklyChallenge = null
      }
    },
    selectEmotion(emotionName) {
      this.selectedEmotion = emotionName
      this.$emit('select-emotion', emotionName)
    },
    selectActivity(activityType) {
      // Route to different components based on activity type
      switch (activityType) {
        case 'word':
          this.$router.push('/language-learning')
          break
        case 'puzzle':
          this.$router.push('/puzzle-game')
          break
        case 'thought':
          this.$router.push('/reflection')
          break
        case 'dictation':
          this.$router.push('/language-learning?tab=dictation')
          break
        default:
      }
    },

    goToTask(taskId) {
      // Navigate to task detail page
      this.$router.push(`/tasks/${taskId}`)
    },

    viewChallenge() {
      // Navigate to selected challenge if available
      if (this.weeklyChallenge && this.weeklyChallenge.id) {
        this.$router.push(`/challenges/${this.weeklyChallenge.id}`)
      } else {
        // Fallback: try to fetch available challenges and go to the first one
        import('../services/challengeService.js')
          .then(({ getAllChallengesForUser }) => getAllChallengesForUser(this.currentUser.id))
          .then((res) => {
            const list = Array.isArray(res?.data) ? res.data : []
            if (list.length > 0 && list[0].id) {
              this.$router.push(`/challenges/${list[0].id}`)
            } else {
              this.showFeedback('No active challenges. Join a challenge to view details.', 'info')
            }
          })
          .catch(() => this.showFeedback('Could not load challenge details. Please try again.', 'error'))
      }
    },
    browseChallenges() {
      this.$router.push('/challenges')
    },

    getStudentAvatar(userId, isCurrentUser = false) {
      // Generate consistent avatars for students
      if (isCurrentUser || userId === this.currentUser?.id) {
        return '👨‍🎓' // Current user gets student icon
      }
      
      // Use different avatars for other students based on user ID
      const avatars = ['👧', '👦', '👩‍🎓', '👨‍🎓', '🧑‍🎓', '👱‍♀️', '👱‍♂️']
      const index = userId ? userId % avatars.length : 0
      return avatars[index]
    },

    showFeedback(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px; max-width: 400px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 6 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 6000);
    },

    viewAllBadges() {
      // Navigate to RewardsPage to view all badges
      this.$router.push('/rewards')
    },

    async completePriorityTask() {
      try {
        if (!this.priorityTask) return
        const { updateTaskStatus } = await import('../services/taskService.js')
        await updateTaskStatus(this.priorityTask.id, 'Completed')
        this.priorityTask.completed = true
        this.showTaskCompletionFeedback()
      } catch (e) {
        console.error('Failed to complete task:', e)
      }
    },

    readFeaturedStory() {
      // Navigate to Library page to start reading
      // If we have a featured story, we could potentially navigate directly to it
      // For now, navigate to library where user can find and read the story
      this.$router.push('/library')
    },

    showTaskCompletionFeedback() {
      // Create a temporary success message
      const successMessage = document.createElement('div')
      successMessage.innerHTML = `
        <div class="alert alert-success alert-dismissible fade show position-fixed" 
             style="top: 20px; right: 20px; z-index: 9999;">
          <strong>Great job! 🎉</strong> You've made progress on your reading task!
          <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        </div>
      `
      document.body.appendChild(successMessage)
      
      // Auto-remove after 3 seconds
      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage)
        }
      }, 3000)
    },

    updateTaskProgress() {
      // Store progress in localStorage (in a real app, this would be sent to backend)
      const progressData = {
        userId: this.currentUser?.id,
        taskType: 'reading',
        progress: this.readingProgress,
        lastUpdated: new Date().toISOString()
      }
      localStorage.setItem('taskProgress', JSON.stringify(progressData))
    },

    loadTaskProgress() {
      // Load existing progress from localStorage
      const savedProgress = localStorage.getItem('taskProgress')
      if (savedProgress) {
        try {
          const progressData = JSON.parse(savedProgress)
          if (progressData.userId === this.currentUser?.id) {
            this.readingProgress = progressData.progress
          }
        } catch (error) {
          console.error('Error loading task progress:', error)
        }
      }
    },

    getWelcomeMessage() {
      // Implement the logic to determine the welcome message based on the current time
      const currentTime = new Date().getHours()
      if (currentTime < 12) {
        return 'Good Morning!'
      } else if (currentTime < 18) {
        return 'Good Afternoon!'
      } else {
        return 'Good Evening!'
      }
    },

    getSubtitleMessage() {
      // Implement the logic to determine the subtitle message based on the current time
      const currentTime = new Date().getHours()
      if (currentTime < 12) {
        return 'Start your day with some learning!'
      } else if (currentTime < 18) {
        return 'Keep up the good work!'
      } else {
        return 'End your day with some learning!'
      }
    },

    setActiveTab(tab) {
      this.activeTab = tab
    }
  }
}
</script>

<style scoped>
/* Container */
.container-fluid {
  background-color: var(--bg-secondary);
  min-height: 100vh;
}

/* Welcome Header */
.fade-in {
  animation: fadeIn 0.5s ease-in;
}

.gradient-text {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Navigation Tabs */
.nav-pills {
  display: flex;
  gap: 0.5rem;
  background: var(--bg-primary);
  padding: 0.5rem;
  border-radius: 12px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
  flex-wrap: wrap;
}

.nav-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  white-space: nowrap;
}

.nav-pill:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.nav-pill.active {
  background: var(--role-primary);
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.nav-pill i {
  font-size: 1rem;
}

/* Tab Content */
.tab-content {
  margin-top: 1rem;
}

/* Greeting Section */
.greeting-section {
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
}

.greeting-icon {
  font-size: 32px;
}

/* Greeting Text */
.greeting-section h3 {
  color: var(--text-primary);
}

.greeting-section p {
  color: var(--text-secondary);
}

/* Emotion Card */
.emotion-card {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
}

.emotion-card p {
  color: var(--text-primary);
}

.emotion-selector {
  display: flex;
  gap: 8px;
}

.emotion-item {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
}

.emotion-item:hover {
  transform: scale(1.1);
  background: var(--bg-tertiary);
}

.emotion-item.selected {
  background: var(--role-primary);
  border-color: var(--role-primary);
  transform: scale(1.1);
}

/* Dashboard Cards */
.dashboard-card {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
  height: 100%;
}

.dashboard-card h5,
.dashboard-card h6 {
  color: var(--text-primary);
}

/* Badges Card */
.badges-card {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.badges-card h5,
.badges-card p,
.badges-card .badge-count,
.badges-card * {
  color: #065f46 !important;
}

.star-filled {
  font-size: 24px;
  margin-right: 4px;
}

.badge-count {
  color: #065f46;
  font-weight: 600;
}

.progress-container {
  width: 100%;
  height: 8px;
  background: rgba(6, 95, 70, 0.2);
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-custom {
  width: 100%;
  height: 100%;
  background: rgba(6, 95, 70, 0.2);
  border-radius: 4px;
}

.progress-fill {
  height: 100%;
  background: #10b981;
  transition: width 0.3s ease;
}

.btn-view-badges {
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
}

/* Priority Task Card */
.priority-task-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.priority-task-card h6,
.priority-task-card p,
.priority-task-card .task-label,
.priority-task-card * {
  color: #92400e !important;
}

.task-icon {
  font-size: 24px;
}

.task-label {
  color: #92400e;
  font-size: 14px;
}

.progress-circle-container {
  position: relative;
}

.progress-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.progress-text {
  text-align: center;
  color: white;
}

.progress-number {
  font-size: 18px;
  font-weight: bold;
  display: block;
}

.progress-unit {
  font-size: 10px;
  display: block;
}

.btn-mark-done {
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 600;
}

/* Stories Card */
.stories-card {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
}

.stories-card h6,
.stories-card p,
.stories-card .story-meta,
.stories-card .story-description,
.stories-card * {
  color: #9d174d !important;
}

.story-icon {
  font-size: 48px;
  text-align: center;
}

.story-meta {
  color: #be185d;
  font-size: 14px;
}

.story-description {
  color: #9d174d;
  font-size: 14px;
}

.btn-read-now {
  background: #ec4899;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 600;
}

/* Leaderboard Card */
.leaderboard-item {
  padding: 8px 0;
}

.leaderboard-item p {
  color: var(--text-primary);
}

.rank-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
}

.rank-1 {
  background: #f59e0b;
}

.rank-2 {
  background: #6b7280;
}

.rank-3 {
  background: #cd7c2f;
}

.student-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-emoji {
  font-size: 24px;
}

.view-all-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 14px;
}

.view-all-link:hover {
  color: var(--role-primary);
}

/* Daily Insights */
.insights-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.activity-item {
  cursor: pointer;
  padding: 16px;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
}

.activity-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
  border-color: var(--role-primary);
}

.activity-item p {
  color: var(--text-primary);
}

.activity-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  margin: 0 auto;
}

/* Task Cards */
.task-card {
  border-radius: 12px;
  color: white;
}

.task-card h6,
.task-card p,
.task-card small,
.task-card * {
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.task-icon-large {
  font-size: 32px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
}

.btn-task {
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  color: white;
}

/* Challenge Card */
.challenge-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.challenge-card h6,
.challenge-card p,
.challenge-card .text-muted,
.challenge-card * {
  color: #92400e !important;
}

.challenge-icon {
  font-size: 32px;
}

.btn-challenge {
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .insights-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .activity-item {
    padding: 12px;
  }
  
  .activity-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
}
</style>